import { Component, computed, DestroyRef, input, output, signal, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControlStatus, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatOption } from '@angular/material/autocomplete';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput, MatSuffix } from '@angular/material/input';
import { MatList, MatListItem } from '@angular/material/list';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { CountryForGetCountriesResponse } from 'app/shared/countries/countries.types';
import { LegalDocumentsService } from 'app/shared/legal-documents/legal-documents.service';
import { LegalDocumentData } from 'app/shared/legal-documents/legal-documents.types';
import { ApiResult } from 'app/shared/types/api-result.types';
import { Gender } from 'app/shared/types/common.types';
import { cloneDeep } from 'lodash-es';
import { Observable } from 'rxjs';
import { fuseAnimations } from '../../../../@fuse/animations';
import { APP_REGEXES } from '../../../app.constants';
import { AppConfigService } from '../../../core/configuration/configuration.service';
import { getServerDateFormat } from '../../../core/dates/date-functions';
import { MAX_LENGTHS as CUSTOMER_MAX_LENGTHS } from '../../../modules/fidelity-app/personal-data/personal-data.consts';
import { MAX_LENGTHS as ADDRESS_MAX_LENGTHS } from '../../addresses/addresses.consts';
import { CountriesService } from '../../countries/countries.service';
import { SafePipe } from '../../pipes/safe.pipe';
import { CustomAuthValidators } from '../../validators/auth.validators';
import { PdfModalComponent } from '../pdf-modal/pdf-modal.component';
import { TbDismissableErrorComponent } from '../tb-dismissable-error/tb-dismissable-error.component';
import { RegistrationFormService } from './registration-form.service';
import { RegistrationDto, RegistrationFromCardDto } from './registration-form.types';

@Component({
    selector: 'app-registration-form',
    imports: [
        MatButton,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatInput,
        MatLabel,
        MatProgressSpinner,
        ReactiveFormsModule,
        TranslocoDirective,
        SafePipe,
        MatError,
        MatDivider,
        MatSuffix,
        MatOption,
        MatSelect,
        MatDatepicker,
        MatDatepickerInput,
        MatDatepickerToggle,
        MatHint,
        MatList,
        MatListItem,
        MatSlideToggle,
        MatAutocompleteModule,
        TbDismissableErrorComponent,
    ],
    templateUrl: './registration-form.component.html',
    styleUrl: './registration-form.component.scss',
    animations: fuseAnimations,
})
export class RegistrationFormComponent {
    protected readonly Gender = Gender;
    protected readonly ADDRESS_MAX_LENGTHS = ADDRESS_MAX_LENGTHS;
    protected readonly CUSTOMER_MAX_LENGTHS = CUSTOMER_MAX_LENGTHS;
    registrationForm: FormGroup;
    registrationFormStatus: Signal<FormControlStatus | undefined>;

    private readonly _currentYear = new Date().getFullYear();
    minDate = new Date(this._currentYear - 120, 0, 1);
    maxDate = new Date();

    disableSubmit = computed<boolean>(() => {
        this.registrationFormStatus();
        return this.registrationForm.invalid;
    });

    protected disableCrudActions = this.registerService.disableCrudActions;

    legalDocuments: LegalDocumentData[];
    availableCountries = signal<CountryForGetCountriesResponse[]>([]);
    filterText = signal<string>('');
    filteredCountries = computed<CountryForGetCountriesResponse[]>(() =>
        this.availableCountries().filter((x) => x.name?.toLowerCase().includes(this.filterText()?.toLowerCase()))
    );

    /**
     * Input to turn off card input based on pre-registration question
     */
    hideCardInput = input.required<boolean>();
    /**
     * Signal to turn off card input based on system configuration
     */
    isRegisterFromCardEnabled = signal<boolean>(false);
    /**
     * Computed signal to determine whether the card input should be displayed or not
     */
    shouldShowCardInput = computed<boolean>(() => !this.hideCardInput() && this.isRegisterFromCardEnabled());

    openPdfInModal = input<boolean>(false);
    fullErrorMessage = input<string>();
    onDismiss = output();
    onRegister = output<Observable<ApiResult<string>>>();

    constructor(
        private fb: FormBuilder,
        private legalDocumentsService: LegalDocumentsService,
        private countriesService: CountriesService,
        private translocoService: TranslocoService,
        private registerService: RegistrationFormService,
        private dialog: MatDialog,
        private destroyRef: DestroyRef,
        private configService: AppConfigService
    ) {
        this.isRegisterFromCardEnabled.set(this.configService.systemConfig.enableRegisterFromCard);

        this.registrationForm = this.fb.group({
            cardNumber: [null],
            email: ['', [Validators.required, Validators.email]],
            password: ['', [Validators.required, CustomAuthValidators.passwordStrengthValidator()]],
            firstName: ['', [Validators.required, Validators.maxLength(CUSTOMER_MAX_LENGTHS.FIRST_NAME)]],
            lastName: ['', [Validators.required, Validators.maxLength(CUSTOMER_MAX_LENGTHS.LAST_NAME)]],
            phoneNumber: [
                null,
                [
                    Validators.pattern(APP_REGEXES.PHONE_NUMBER),
                    Validators.maxLength(ADDRESS_MAX_LENGTHS.PHONE_NUMBER_A),
                ],
            ],
            city: [null, [Validators.required, Validators.maxLength(ADDRESS_MAX_LENGTHS.CITY)]],
            zipCode: [null, [Validators.required, Validators.maxLength(ADDRESS_MAX_LENGTHS.POSTAL_CODE)]],
            country: [null],
            birthDate: [null],
            gender: [Gender.unspecified, [Validators.required]],
            consentTerms: [false, [Validators.requiredTrue]],
            consentMarketing: [false],
            consentProfiling: [false],
        });
        this.registrationFormStatus = toSignal(this.registrationForm.statusChanges);

        this.fetchLegalDocs();

        this.loadAndApplyCountriesTranslations();
        this.translocoService.langChanges$
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.loadAndApplyCountriesTranslations());
    }

    private loadAndApplyCountriesTranslations() {
        //Before fetching the countries, we need to make sure that the countries translations are loaded
        //as we use them to display the localized name in the form.
        this.translocoService
            .load('countries/' + this.translocoService.getActiveLang())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.countriesService
                    .getCountries()
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((res) => {
                        this.availableCountries.set(
                            res.result.countries.map((x) => {
                                x.name = this.translocoService.translate('countries.' + x.alpha2);
                                return x;
                            })
                        );

                        const countryControl = this.registrationForm.get('country');
                        if (countryControl.value) {
                            let translatedCountry = this.availableCountries().find(
                                (x) => x.id == countryControl.value.id
                            );
                            countryControl.patchValue(translatedCountry);
                            this.registrationForm.updateValueAndValidity();
                        }
                    });
            });
    }

    fetchLegalDocs() {
        this.legalDocumentsService
            .getLegalDocumentsForCustomerOperations(this.translocoService.getActiveLang())
            .subscribe((res) => {
                this.legalDocuments = res.result;
            });
    }

    filterCountries($event: any) {
        this.filterText.set($event.target.value);
    }

    getOptionName = (option: CountryForGetCountriesResponse) => option?.name;

    register: Function = () => {
        if (
            this.hideCardInput() ||
            this.registrationForm.value.cardNumber == null ||
            this.registrationForm.value.cardNumber == ''
        ) {
            let dto: RegistrationDto = cloneDeep(this.registrationForm.value);
            dto.birthDate = getServerDateFormat(this.registrationForm.value.birthDate);
            dto.countryId = this.registrationForm.value.country?.id;
            this.onRegister.emit(this.registerService.registerNew(dto));
        } else {
            let dto: RegistrationFromCardDto = cloneDeep(this.registrationForm.value);
            dto.birthDate = getServerDateFormat(this.registrationForm.value.birthDate);
            dto.countryId = this.registrationForm.value.country?.id;
            this.onRegister.emit(this.registerService.registerFromCard(dto));
        }
    };

    openModal(pdfUrl: string) {
        this.dialog.open(PdfModalComponent, {
            data: { pdfUrl: pdfUrl },
            height: 'calc(100% - 30px)',
            width: 'calc(100% - 30px)',
            maxWidth: '100%',
            maxHeight: '100%',
        });
    }

    dismissError() {
        this.onDismiss.emit();
    }
}
